import React, { useContext, useState } from 'react'
import {
  AuthenticatedPrescriptionRequest,
  CuratedMedicineConcise,
  Gender,
  IHIdentifier,
  MedicineCategory,
  MedicineWithKeyAcg,
  PrescriptionRequest,
} from '@/lib/apis/types'
import { useStore as useGlobalStore } from '@/lib/store'
import {
  NotificationItem,
  NotificationItemVariant,
} from '@/lib/models/NotificationItem'
import { translateError } from '@/lib/apis/error'
import { Prescription } from '@/lib/questionnaires/Prescription'
import FreeTextMedicineDisplay from '@/features/prescriptions/request-review/FreeTextMedicineDisplay'
import DatabaseMedicineDisplay from '@/features/prescriptions/request-review/DatabaseMedicineDisplay'
import { ScriptRequestFormContext } from '@/features/prescriptions/request/ScriptRequestFormProvider'
import { useStore } from 'zustand'
import { FreeTextPrescriptionContentKey } from '@/features/prescriptions/PrescriptionConstants'
import { useMutation } from '@tanstack/react-query'
import { CreatePrescriptionRequest } from '@/lib/apis/prescription'
import useApiClient from '@/lib/apis/clients/useApiClient'
import {
  PrescriptionQuizFooter,
  prescriptionQuestionItemMotion,
  prescriptionQuestionListMotion,
} from '@/features/prescriptions/request/PrescriptionQuizShell'
import { motion } from 'framer-motion'
import formToDto = Prescription.formToDto
import posthog from 'posthog-js'
import { format, isValid } from 'date-fns'
import { GENDERS } from '@/lib/types'
import { parseDate } from '@/lib/utils'
import {
  QuizReviewCard,
  QuizReviewSection,
  QuizReviewSections,
  type QuizReviewRow,
} from '@/shared/components/quiz/QuizReviewSection'

type MedicineRequested =
  | {
      type: 'database'
      medicine: CuratedMedicineConcise
      medicinesUnderCategory: Array<MedicineWithKeyAcg>
      onUpdateMedicine: (
        medicine: CuratedMedicineConcise,
        preferredBrand?: string,
      ) => void
      category: MedicineCategory
      preferredBrand?: string
    }
  | {
      type: 'freetext'
      medicine: string
      onUpdateMedicine: (value: string) => void
    }

type Props = {
  freetextContent?: string
  requestedMedicine: MedicineRequested
}

type CommunicationTokens = Pick<PrescriptionRequest, 'tokens'>

export function PrescriptionRequestReviewStage({
  freetextContent,
  requestedMedicine,
}: Props) {
  const store = useContext(ScriptRequestFormContext)
  if (!store)
    throw new Error(
      'PrescriptionRequestReviewForm must be used within a ScriptRequestFormProvider',
    )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [medicineDialogOpen, setMedicineDialogOpen] = useState(false)

  const showNotification = useGlobalStore((state) => state.showNotification)

  const onPrev = useStore(store, (s) => s.onPrev)
  const session = useStore(store, (s) => s.session)
  const goToStage = useStore(store, (s) => s.goToStage)
  const questionnaireForm = useStore(store, (s) => s.questionnaireForm)
  const profileFormState = useStore(store, (s) => s.profileFormState)
  const ihiVerificationState = useStore(store, (s) => s.ihiVerificationState)
  const selectedMedicine = useStore(store, (s) => s.selectedMedicine)
  const supportingDocuments = useStore(store, (s) => s.supportingDocuments)
  const showPersonalDetails = useStore(store, (s) =>
    s.stages.some((stage) => stage.type === 'PERSONAL-INFORMATION'),
  )

  const api = useApiClient()
  const prescriptionMutation = useMutation(
    CreatePrescriptionRequest.mutation(api),
  )

  async function submitRequest() {
    setIsSubmitting(() => true)

    try {
      const metadata = {
        medicineId: selectedMedicine?.medicine.id ?? null,
        questionnaire: formToDto(questionnaireForm.values),
        preferredBrand: selectedMedicine?.preferredBrand ?? null,
        medicineFreeText: freetextContent ?? null,
      }

      const data = {
        metadata,
        allowsAutoNoteTakingSoftware:
          questionnaireForm.values.allowsAutoNoteTakingSoftware === 'Yes',
        documents: supportingDocuments.map((document, idx) => {
          if (document.uploadState.uploading)
            throw new Error('Invalid upload state for the documents')

          return {
            token: document.uploadState.token,
            description: supportingDocuments[idx].description,
          }
        }),
        ...(!session
          ? {
              profile: {
                type: 'patient' as const,
                ...profileFormState.values,
                gender: profileFormState.values.gender as Gender,
              },
              tokens: computeCommunicationTokens().tokens,
              ihIdentifier: computeIHIdentifierValue(),
            }
          : {}),
        scheduledSlot: null,
      } as PrescriptionRequest | AuthenticatedPrescriptionRequest

      const checkoutResult = await prescriptionMutation.mutateAsync(data)
      localStorage.removeItem(FreeTextPrescriptionContentKey)
      window.location.assign(checkoutResult.checkoutUrl)
    } catch (err) {
      console.error(err)
      posthog.captureException(err)

      showNotification(
        new NotificationItem(
          translateError(err),
          NotificationItemVariant.Error,
        ),
      )
      setIsSubmitting(() => false)
    }
  }

  function computeIHIdentifierValue(): IHIdentifier | null {
    if (!ihiVerificationState.verified) return null
    else if (ihiVerificationState.id === 'MED') {
      return {
        type: 'med',
        number: ihiVerificationState.verified.number,
        irn: ihiVerificationState.verified.irn!,
        expiry: ihiVerificationState.verified.expiry,
      }
    } else if (ihiVerificationState.id === 'IHI') {
      return {
        type: 'ihi',
        ...ihiVerificationState.verified,
      }
    } else {
      return null
    }
  }

  function computeCommunicationTokens(): CommunicationTokens {
    let mobile = null
    if (
      profileFormState.verification.mobileVerificationResult
        ?.alreadyVerified === false
    ) {
      mobile = profileFormState.verification.mobileVerificationResult.token
    }

    return {
      tokens: {
        email: null,
        mobile,
      },
    }
  }

  const answerRows = buildAnswerRows(questionnaireForm.values)
  const detailRows: QuizReviewRow[] = [
    {
      label: 'Name',
      value: `${profileFormState.values.firstName} ${profileFormState.values.lastName}`,
    },
    {
      label: 'Gender',
      value:
        GENDERS[profileFormState.values.gender as keyof typeof GENDERS] ??
        profileFormState.values.gender,
    },
    { label: 'Email', value: profileFormState.values.email },
    { label: 'Mobile', value: profileFormState.values.mobile },
    {
      label: 'Date of birth',
      value: displayReviewDate(profileFormState.values.dob),
    },
    {
      label: 'Address',
      value: formatReviewAddress(profileFormState.values),
    },
  ]

  return (
    <motion.div
      animate="animate"
      className="flex flex-col"
      initial="initial"
      variants={prescriptionQuestionListMotion}
    >
      <QuizReviewSections>
        <motion.div variants={prescriptionQuestionItemMotion}>
          <QuizReviewSection
            onChange={() => setMedicineDialogOpen(true)}
            title="Your medicine"
          >
            <MedicineRequestedReviewSection
              dialogOpen={medicineDialogOpen}
              medicine={requestedMedicine}
              onDialogOpenChange={setMedicineDialogOpen}
            />
          </QuizReviewSection>
        </motion.div>

        <motion.div variants={prescriptionQuestionItemMotion}>
          <QuizReviewSection
            last={supportingDocuments.length === 0 && !showPersonalDetails}
            onChange={() => goToStage('QUESTIONNAIRE', 0)}
            rows={answerRows}
            title="Your answers"
          />
        </motion.div>

        {supportingDocuments.length > 0 && (
          <motion.div variants={prescriptionQuestionItemMotion}>
            <QuizReviewSection
              last={!showPersonalDetails}
              onChange={() => goToStage('UPLOAD-DOCUMENTS')}
              title="Your documents"
            >
              <QuizReviewCard className="py-3.5">
                {supportingDocuments.map((document) => (
                  <div
                    className="flex items-center justify-between gap-4 py-1 text-[14px] leading-5"
                    key={document.name}
                  >
                    <span className="truncate font-medium">
                      {document.name}
                    </span>
                    <span className="shrink-0 text-[12.5px] text-[#82828B]">
                      Uploaded
                    </span>
                  </div>
                ))}
              </QuizReviewCard>
            </QuizReviewSection>
          </motion.div>
        )}

        {showPersonalDetails && (
          <motion.div variants={prescriptionQuestionItemMotion}>
            <QuizReviewSection
              last
              onChange={() => goToStage('PERSONAL-INFORMATION')}
              rows={detailRows}
              title="Your details"
            />
          </motion.div>
        )}
      </QuizReviewSections>

      <motion.div variants={prescriptionQuestionItemMotion}>
        <PrescriptionQuizFooter
          backDisabled={isSubmitting}
          nextLabel="Continue to payment"
          nextLoading={isSubmitting}
          note="You’re only charged once the doctor approves your request."
          onBack={onPrev}
          onNext={() => void submitRequest()}
        />
      </motion.div>
    </motion.div>
  )
}

type MedicineRequestedReviewSectionProps = {
  medicine: MedicineRequested
  dialogOpen: boolean
  onDialogOpenChange: (open: boolean) => void
}

function MedicineRequestedReviewSection({
  medicine,
  dialogOpen,
  onDialogOpenChange,
}: MedicineRequestedReviewSectionProps) {
  if (medicine.type === 'database') {
    return (
      <DatabaseMedicineDisplay
        medicine={medicine.medicine}
        medicinesUnderCategory={medicine.medicinesUnderCategory}
        onUpdateMedicine={medicine.onUpdateMedicine}
        preferredBrand={medicine.preferredBrand}
        category={medicine.category}
        variant="quiz"
        hideActions
        medicineSelectDialogOpen={dialogOpen}
        onMedicineSelectDialogOpenChange={onDialogOpenChange}
      />
    )
  }

  return (
    <QuizReviewCard>
      <FreeTextMedicineDisplay
        editDialogOpen={dialogOpen}
        medicine={medicine.medicine}
        onChange={medicine.onUpdateMedicine}
        onEditDialogOpenChange={onDialogOpenChange}
        variant="review"
      />
    </QuizReviewCard>
  )
}

function buildAnswerRows(values: Prescription.QuestionnaireValues) {
  const duration = [
    values.durationYear ? `${values.durationYear} years` : '',
    values.durationMonth ? `${values.durationMonth} months` : '',
  ]
    .filter(Boolean)
    .join(', ')

  const entries: Array<[string, unknown]> = [
    ['Taking it for', values.condition],
    ['Prescribed by your regular GP', values.isPrescribedByRegularGP],
    ['Taking it since', duration],
    ['Symptoms controlled on this dose', values.isSameDose],
    [
      'Side effects',
      values.hasSideEffects === 'No' ? 'None' : values.hasSideEffects,
    ],
    ['Pregnant or trying', values.isOrTryingToBePregnant],
    ['Breastfeeding', values.isBreastFeeding],
    ['Significant conditions', values.hasSignificantMedicalCondition],
    ['Condition details', values.significantMedicalConditions],
    ['Other medicines', values.isOnOtherMedications],
    ['Medicine details', values.otherMedications],
    ['Allergies', values.hasAllergy],
    ['Allergy details', values.allergies],
    ['GP follow-up', values.followUpAgreement],
    ['In Australia', values.inAustraliaAgreement],
    ['Secure note taking', values.allowsAutoNoteTakingSoftware],
  ]

  return entries
    .filter(([, value]) => String(value ?? '').trim().length > 0)
    .map(([label, value]) => ({
      label,
      value:
        value === 'NotApplicable'
          ? 'Not applicable'
          : String(value ?? '').trim(),
    }))
}

function displayReviewDate(value?: string | null) {
  if (!value) return ''
  const parsed = parseDate(value)
  return isValid(parsed) ? format(parsed, 'd MMMM yyyy') : value
}

function formatReviewAddress(values: {
  formattedAddress?: string | null
  suburb?: string | null
  state?: string | null
  postcode?: string | null
}) {
  return [
    values.formattedAddress,
    values.suburb,
    [values.state, values.postcode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')
}
